import dynamic from "next/dynamic";

import { useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faSync } from "@fortawesome/free-solid-svg-icons/faSync";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useNotification from "components/Notification/useNotification";
import useAuthentication from "hooks/useAuthentication";
import { fetchApi } from "utils/baseFetcher";

const RepublishPrompt = dynamic(() => import("./RepublishPrompt"), {
  ssr: false,
});

const RepublishButton = ({
  insight,
  onUpdate,
  className,
}: {
  insight: Insight;
  onUpdate?: () => void;
  className?: string;
}) => {
  const { account: loggedInAccount, mutateAccount } = useAuthentication();
  const { setNotification } = useNotification();

  const quotaLeft = 4 - (loggedInAccount?.currentMonthRepublishedInsights || 0);
  const canRepublish =
    insight.isPublished &&
    insight.account?.id === loggedInAccount?.id &&
    quotaLeft > 0;

  const [showPrompt, togglePrompt] = useToggle();

  const republish = useCallback(
    (reason: string) => {
      fetchApi(`/api/v2/insights/${insight.id}/republish`, {
        method: "PATCH",
        body: JSON.stringify({
          data: {
            id: insight.id,
            type: "insights",
            attributes: { "boomerang-reason": reason },
          },
        }),
      }).then(() => {
        setNotification("Your insight has been boomeranged.", "success");
        togglePrompt();
        mutateAccount();
        onUpdate?.();
      });
    },
    [insight.id, mutateAccount, onUpdate, togglePrompt, setNotification]
  );

  if (!canRepublish) return null;

  return (
    <>
      <OverlayTrigger
        overlay={<Tooltip>{"Resurface this Insight in users' feeds"}</Tooltip>}
      >
        <button
          className={
            className ||
            twMerge(
              "outline-none bg-white",
              "rounded-full border border-solid border-smart-teal",
              "px-[10px] py-[6px]",
              "text-smart-teal text-[12px] leading-[12px]"
            )
          }
          onClick={togglePrompt}
        >
          <FontAwesomeIcon icon={faSync} className="mr-[5px]" />
          Boomerang
        </button>
      </OverlayTrigger>
      {showPrompt && (
        <RepublishPrompt
          quotaLeft={quotaLeft}
          onConfirm={republish}
          onClose={togglePrompt}
        />
      )}
    </>
  );
};

export default RepublishButton;
